import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { deviceId, pullPrefs } from "@/lib/prefsSync";
import { loadPlayer } from "@/lib/offline";

const MAX_AGE_MS = 7 * 24 * 3600 * 1000;
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/* On sign-in: pull account settings, then offer to pick up the queue last saved from another device. */
export const ResumeSync = () => {
  const p = usePlayer();
  const { user } = useAuth();
  const pRef = useRef(p);
  pRef.current = p;
  useEffect(() => {
    if (!user?.id || user.offline) return;
    let alive = true;
    (async () => {
      await pullPrefs().catch(() => {});
      const { data } = await api.get("/prefs/player").catch(() => ({ data: null }));
      if (!alive || !data?.tracks?.length || data.device_id === deviceId()) return;
      const age = Date.now() - new Date(data.updated_at).getTime();
      const local = loadPlayer();
      const sameSpot = local?.queue?.[local.index]?.id === data.tracks[data.index]?.id;
      if (age > MAX_AGE_MS || sameSpot || localStorage.getItem("rh.resumeSeen") === data.updated_at) return;
      localStorage.setItem("rh.resumeSeen", data.updated_at);
      const t = data.tracks[data.index];
      const where = data.device ? `on ${data.device}` : "on another device";
      const play = () => pRef.current.playTracks(data.tracks, data.index, data.time);
      if (!pRef.current.queue.length) {
        pRef.current.loadTracks(data.tracks, data.index, data.time);
        toast(`Picked up where you left off ${where}`, { description: `${t.artist} — ${t.title} · ${fmt(data.time)}`, duration: 10000, action: { label: "Play", onClick: play }, id: "resume-sync" });
      } else {
        toast(`Continue ${where}?`, { description: `${t.artist} — ${t.title} · ${fmt(data.time)}`, duration: 12000, action: { label: "Resume", onClick: play }, id: "resume-sync" });
      }
    })();
    return () => { alive = false; };
  }, [user?.id, user?.offline]);
  return null;
};
